import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CHARGE_TYPES, PAYMENT_METHODS, type ChargeType, type PaymentMethod } from '@gwc/shared';
import { billingApi } from '../api/billing';
import { membersApi } from '../api/members';
import { ApiError } from '../lib/api';

const money = (n: number | string) =>
  Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

type Tab = 'charges' | 'invoices' | 'payments';

export function Billing() {
  const [tab, setTab] = useState<Tab>('charges');
  const qc = useQueryClient();

  const membersQuery = useQuery({ queryKey: ['members'], queryFn: membersApi.list });
  const chargesQuery = useQuery({
    queryKey: ['billing', 'charges'],
    queryFn: () => billingApi.listCharges({ status: 'pending' }),
  });
  const invoicesQuery = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => billingApi.listInvoices(),
  });
  const paymentsQuery = useQuery({
    queryKey: ['billing', 'payments'],
    queryFn: () => billingApi.paymentHistory(),
  });

  const [personId, setPersonId] = useState('');
  const [chargeType, setChargeType] = useState<ChargeType>('custom');
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [periodYear, setPeriodYear] = useState(String(new Date().getFullYear()));
  const [selectedCharges, setSelectedCharges] = useState<Set<string>>(new Set());
  const [bulkYear, setBulkYear] = useState(String(new Date().getFullYear()));
  const [bulkAmount, setBulkAmount] = useState('150');

  const createChargeMut = useMutation({
    mutationFn: () =>
      billingApi.createCharge({
        personId,
        chargeType,
        label: label.trim(),
        amount: Number(amount),
        periodYear: chargeType === 'annual_dues' ? Number(periodYear) : null,
        dueDate: dueDate || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['billing'] });
      setLabel('');
      setAmount('');
    },
  });

  const bulkAnnualMut = useMutation({
    mutationFn: () =>
      billingApi.bulkAnnualDues({
        year: Number(bulkYear),
        amount: Number(bulkAmount),
        dueDate: dueDate || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing'] }),
  });

  const createInvoiceMut = useMutation({
    mutationFn: (memberId: string) =>
      billingApi.createInvoice({
        personId: memberId,
        chargeIds: [...selectedCharges],
        issueNow: true,
        dueDate: dueDate || null,
      }),
    onSuccess: () => {
      setSelectedCharges(new Set());
      qc.invalidateQueries({ queryKey: ['billing'] });
    },
  });

  function toggleCharge(id: string) {
    setSelectedCharges((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const memberName = (id: string) => {
    const m = membersQuery.data?.find((x) => x.id === id);
    return m ? `${m.firstName} ${m.lastName}` : id.slice(0, 8);
  };

  return (
    <div className="space-y-8">
      <div>
        <Link to="/club" className="text-sm text-cream-300/60 hover:text-gold-200">
          ← Club
        </Link>
        <h1 className="mt-2 font-display text-3xl text-cream-50">Billing</h1>
        <p className="mt-1 text-sm text-cream-300/70">
          Track charges, generate invoices, and record payments. Online card checkout plugs in when Stripe is configured.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {(['charges', 'invoices', 'payments'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`rounded-full px-4 py-1.5 text-sm capitalize ${tab === t ? 'bg-gold-600/30 text-gold-200' : 'border border-ink-800 text-cream-300/70'}`}
            onClick={() => setTab(t)}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'charges' && (
        <div className="grid gap-6 lg:grid-cols-2">
          <section className="card space-y-4 p-6">
            <h2 className="font-display text-xl text-cream-50">New charge</h2>
            <form
              className="space-y-3"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                createChargeMut.mutate();
              }}
            >
              <select className="field" value={personId} onChange={(e) => setPersonId(e.target.value)} required>
                <option value="">Select member…</option>
                {(membersQuery.data ?? []).map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.firstName} {m.lastName}
                  </option>
                ))}
              </select>
              <select className="field" value={chargeType} onChange={(e) => setChargeType(e.target.value as ChargeType)}>
                {CHARGE_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.replace('_', ' ')}
                  </option>
                ))}
              </select>
              <input className="field" placeholder="Label" value={label} onChange={(e) => setLabel(e.target.value)} required />
              <input
                className="field"
                type="number"
                step="0.01"
                min="0"
                placeholder="Amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
              {chargeType === 'annual_dues' && (
                <input className="field" type="number" value={periodYear} onChange={(e) => setPeriodYear(e.target.value)} />
              )}
              <input className="field" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              <button type="submit" className="btn-primary text-sm" disabled={createChargeMut.isPending}>
                {createChargeMut.isPending ? 'Saving…' : 'Create charge'}
              </button>
              {createChargeMut.isError && (
                <p className="text-sm text-wine-300">
                  {createChargeMut.error instanceof ApiError ? createChargeMut.error.message : 'Failed.'}
                </p>
              )}
            </form>
          </section>

          <section className="card space-y-4 p-6">
            <h2 className="font-display text-xl text-cream-50">Bulk annual dues</h2>
            <p className="text-sm text-cream-300/70">Creates a pending charge for every active member.</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <input className="field" type="number" value={bulkYear} onChange={(e) => setBulkYear(e.target.value)} />
              <input
                className="field"
                type="number"
                step="0.01"
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={bulkAnnualMut.isPending}
              onClick={() => bulkAnnualMut.mutate()}
            >
              {bulkAnnualMut.isPending ? 'Creating…' : 'Charge all active members'}
            </button>
            {bulkAnnualMut.isSuccess && (
              <p className="text-sm text-gold-200">Created {bulkAnnualMut.data?.created ?? 0} charges.</p>
            )}
          </section>

          <section className="card p-6 lg:col-span-2">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="font-display text-xl text-cream-50">Pending charges</h2>
              {selectedCharges.size > 0 && (
                <button
                  type="button"
                  className="btn-primary text-sm"
                  disabled={createInvoiceMut.isPending}
                  onClick={() => {
                    const first = chargesQuery.data?.data.find((c) => selectedCharges.has(c.id));
                    if (first) createInvoiceMut.mutate(first.personId);
                  }}
                >
                  Invoice selected ({selectedCharges.size})
                </button>
              )}
            </div>
            {chargesQuery.isLoading ? (
              <p className="text-sm text-cream-300/50">Loading…</p>
            ) : (chargesQuery.data?.data.length ?? 0) === 0 ? (
              <p className="text-sm text-cream-300/50">No pending charges.</p>
            ) : (
              <ul className="divide-y divide-ink-800">
                {chargesQuery.data!.data.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 py-3 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedCharges.has(c.id)}
                      onChange={() => toggleCharge(c.id)}
                      disabled={Boolean(
                        selectedCharges.size > 0 &&
                          !selectedCharges.has(c.id) &&
                          chargesQuery.data?.data.find((x) => selectedCharges.has(x.id))?.personId !== c.personId,
                      )}
                    />
                    <div className="flex-1">
                      <span className="text-cream-100">{c.label}</span>
                      <span className="ml-2 text-cream-300/50">· {memberName(c.personId)}</span>
                      <p className="text-xs text-cream-300/40">
                        {money(c.amount)} · {c.chargeType.replace('_', ' ')}
                      </p>
                    </div>
                    <RecordManualButton chargeId={c.id} amount={Number(c.amount)} onDone={() => qc.invalidateQueries({ queryKey: ['billing'] })} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {tab === 'invoices' && (
        <section className="card p-6">
          <h2 className="mb-4 font-display text-xl text-cream-50">Invoices</h2>
          {invoicesQuery.isLoading ? (
            <p className="text-sm text-cream-300/50">Loading…</p>
          ) : (invoicesQuery.data?.data.length ?? 0) === 0 ? (
            <p className="text-sm text-cream-300/50">No invoices yet.</p>
          ) : (
            <ul className="divide-y divide-ink-800">
              {invoicesQuery.data!.data.map((inv) => (
                <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 py-3 text-sm">
                  <div>
                    <Link to={`/billing/invoices/${inv.id}`} className="text-gold-200 hover:underline">
                      {inv.invoiceNumber}
                    </Link>
                    <span className="ml-2 text-cream-300/50">{memberName(inv.personId)}</span>
                    <p className="text-xs text-cream-300/40">
                      {money(inv.subtotal)} · {inv.status}
                      {inv.dueDate ? ` · due ${inv.dueDate}` : ''}
                    </p>
                  </div>
                  {inv.status === 'open' && (
                    <RecordManualButton
                      invoiceId={inv.id}
                      amount={Number(inv.subtotal) - Number(inv.amountPaid)}
                      onDone={() => qc.invalidateQueries({ queryKey: ['billing'] })}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {tab === 'payments' && (
        <section className="card p-6">
          <h2 className="mb-4 font-display text-xl text-cream-50">Payment history</h2>
          {paymentsQuery.isLoading ? (
            <p className="text-sm text-cream-300/50">Loading…</p>
          ) : (paymentsQuery.data?.data.length ?? 0) === 0 ? (
            <p className="text-sm text-cream-300/50">No payments recorded.</p>
          ) : (
            <ul className="divide-y divide-ink-800">
              {paymentsQuery.data!.data.map((p) => (
                <li key={p.id} className="py-3 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="text-cream-100">{memberName(p.personId)}</span>
                    <span className="text-gold-200">{money(p.amount)}</span>
                  </div>
                  <p className="text-xs capitalize text-cream-300/40">
                    {p.status} · {p.provider}
                    {p.paymentMethod ? ` · ${p.paymentMethod}` : ''} · {new Date(p.createdAt).toLocaleDateString()}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function RecordManualButton({
  chargeId,
  invoiceId,
  amount,
  onDone,
}: {
  chargeId?: string;
  invoiceId?: string;
  amount: number;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState<PaymentMethod>('check');
  const mut = useMutation({
    mutationFn: () =>
      billingApi.recordManualPayment({
        chargeId,
        invoiceId,
        amount,
        paymentMethod: method,
      }),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });

  if (!open) {
    return (
      <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => setOpen(true)}>
        Record payment
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <select className="field py-1 text-xs" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
        {PAYMENT_METHODS.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <button type="button" className="btn-primary px-2 py-1 text-xs" disabled={mut.isPending} onClick={() => mut.mutate()}>
        Save
      </button>
      <button type="button" className="btn-ghost px-2 py-1 text-xs" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </div>
  );
}
